import type { ReactNode } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';

import type { EngineOil } from '@/generated/GeneratedApiTypes';

interface PreconditionItemProps {
    isValid?: boolean;
    messageId: string;
    values?: Record<string, (chunks: ReactNode) => ReactNode>;
    disabled?: boolean;
    index: number;
}

const PreconditionItem = ({ isValid, messageId, values, disabled, index }: PreconditionItemProps) => {
    let preconditionIcon = '';
    let preconditionItemColor = '';

    if (isValid === undefined || disabled) {
        preconditionIcon = 'rioglyph-minus-sign text-color-dark';
        preconditionItemColor = 'text-color-dark';
    } else if (isValid) {
        preconditionIcon = 'rioglyph-ok-sign text-color-success';
    } else {
        preconditionIcon = 'rioglyph-remove-sign text-color-danger';
    }

    return (
        <p className={preconditionItemColor}>
            <span className={`rioglyph text-size-h3 margin-right-10 ${preconditionIcon}`} />
            {index}. <FormattedMessage id={messageId} values={values} />
        </p>
    );
};

const bold = { b: (chunks: ReactNode) => <b>{chunks}</b> };

const Tbm3DidPreconditionsDynamic = ({ engineOil }: { engineOil: EngineOil }) => {
    const preconditions = engineOil.preconditions;
    const measuredAt = preconditions?.measured_at?.date;

    // If External Tester Disconnected is false, we should disable others PreConditions
    const disablePrecondition = !preconditions?.is_external_tester_disconnected;

    const measuredAtDatetime = measuredAt ? new Date(measuredAt) : null;
    const isMoreThan24HoursAgo =
        measuredAtDatetime && !Number.isNaN(measuredAtDatetime.getTime())
            ? Date.now() - measuredAtDatetime.getTime() > 24 * 60 * 60 * 1000
            : false;

    return (
        <div>
            <span className="text-bold">
                <FormattedMessage
                    id="intl-msg:engineOil.preconditions.tbm3Did.dynamic.statusAt"
                    values={{
                        dt: (
                            <>
                                <FormattedDate value={measuredAt} day="2-digit" month="2-digit" year="numeric" />{' '}
                                <FormattedTime value={measuredAt} />
                            </>
                        ),
                    }}
                />
            </span>

            <div className="margin-top-25">
                <PreconditionItem
                    isValid={preconditions?.is_external_tester_disconnected}
                    messageId="intl-msg:engineOil.preconditions.tbm3Did.dynamic.externalConnectors"
                    disabled={false}
                    index={1}
                />
                <PreconditionItem
                    isValid={preconditions?.is_standing_on_flat_surface}
                    messageId="intl-msg:engineOil.preconditions.tbm3Did.dynamic.flatSurface"
                    disabled={disablePrecondition}
                    index={2}
                />
                <PreconditionItem
                    isValid={preconditions?.is_standing_time_valid}
                    messageId="intl-msg:engineOil.preconditions.tbm3Did.dynamic.standingTime"
                    values={bold}
                    disabled={disablePrecondition}
                    index={3}
                />
                <PreconditionItem
                    isValid={preconditions?.is_ignition_on_valid}
                    messageId="intl-msg:engineOil.preconditions.tbm3Did.dynamic.ignition"
                    values={bold}
                    disabled={disablePrecondition}
                    index={4}
                />
                <PreconditionItem
                    isValid={preconditions?.is_engine_not_running}
                    messageId="intl-msg:engineOil.preconditions.tbm3Did.dynamic.engine"
                    values={bold}
                    disabled={disablePrecondition}
                    index={5}
                />
                <PreconditionItem
                    isValid={preconditions?.is_engine_oil_level_valid && !isMoreThan24HoursAgo}
                    messageId="intl-msg:engineOil.preconditions.tbm3Did.dynamic.signal"
                    disabled={disablePrecondition}
                    index={6}
                />
            </div>

            <div className="margin-top-25">
                <p>
                    <FormattedMessage id="intl-msg:engineOil.preconditions.tbm3Did.dynamic.moreDetails" />
                </p>
                <p className="text-bold">
                    <FormattedMessage id="intl-msg:engineOil.preconditions.tbm3Did.dynamic.fulfillAllForValid" />
                </p>
            </div>
        </div>
    );
};

export default Tbm3DidPreconditionsDynamic;
